<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import api from '@/api';
import SkeletonSentence from '@/components/common/atoms/skeleton-sentence.vue';
import { ExcludedKeyword, Keyword } from '@/types/themeQueryGenerationRequest';
import {
  getAllSiteCategories,
  getSiteCategoriesValueForSearch,
} from '@/utils/siteCategories';
import { useTeamInfo } from '@/utils/swr';

export interface Props {
  lang: 'ja' | 'en';
  word: (Keyword | ExcludedKeyword) & { description?: string };
  isDeletedKeywordSelected: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'delete-keyword', uuid: string, lang: 'ja' | 'en'): void;
  (e: 'restore-keyword', uuid: string, lang: 'ja' | 'en'): void;
}>();

const popupRef = ref<HTMLElement | null>(null);
defineExpose({ popupRef });
const isLoading = ref<boolean>(true);
const articleJaCount = ref<number>(0);
const articleEnCount = ref<number>(0);
const researchPaperJaCount = ref<number>(0);
const researchPaperEnCount = ref<number>(0);
const patentJaCount = ref<number>(0);

const { data: teamInfo } = useTeamInfo();

const isEnableForeignLangMedia = computed(
  () => teamInfo.value?.enable_foreign_lang_media,
);
const isTechnicalResearchAvailable = computed(
  () => teamInfo.value?.enable_technical_literature,
);

const deleteKeyword = (uuid: string, lang: 'ja' | 'en') => {
  emit('delete-keyword', uuid, lang);
};
const restoreKeyword = (uuid: string, lang: 'ja' | 'en') => {
  emit('restore-keyword', uuid, lang);
};

onMounted(() => {
  setHitCount();
});

watch(
  () => props.word[props.lang],
  () => {
    setHitCount();
  },
);

const setArticleJaCount = async () => {
  const res = await api.getThemePreviewDocuments(
    'article',
    [props.word[props.lang]],
    0,
    'ja',
    true,
    [],
    getSiteCategoriesValueForSearch(getAllSiteCategories()),
    true,
  );
  articleJaCount.value = res.count;
};
const setArticleEnCount = async () => {
  const res = await api.getThemePreviewDocuments(
    'article',
    [props.word[props.lang]],
    0,
    'en',
    true,
    [],
    getSiteCategoriesValueForSearch(getAllSiteCategories()),
    true,
  );
  articleEnCount.value = res.count;
};
const setResearchPaperJaCount = async () => {
  const res = await api.getThemePreviewDocuments(
    'research_paper',
    [props.word[props.lang]],
    0,
    'ja',
    true,
    [],
    getSiteCategoriesValueForSearch(getAllSiteCategories()),
    true,
  );
  researchPaperJaCount.value = res.count;
};
const setResearchPaperEnCount = async () => {
  const res = await api.getThemePreviewDocuments(
    'research_paper',
    [props.word[props.lang]],
    0,
    'en',
    true,
    [],
    getSiteCategoriesValueForSearch(getAllSiteCategories()),
    true,
  );
  researchPaperEnCount.value = res.count;
};
const setPatentJaCount = async () => {
  const res = await api.getThemePreviewDocuments(
    'patent',
    [props.word[props.lang]],
    0,
    'ja',
    true,
    [],
    getSiteCategoriesValueForSearch(getAllSiteCategories()),
    true,
  );
  patentJaCount.value = res.count;
};
const setHitCount = async () => {
  isLoading.value = true;
  await setArticleJaCount();
  if (isEnableForeignLangMedia.value) {
    await setArticleEnCount();
    if (isTechnicalResearchAvailable.value) {
      await setResearchPaperEnCount();
    }
  }
  if (isTechnicalResearchAvailable.value) {
    await setResearchPaperJaCount();
    await setPatentJaCount();
  }
  isLoading.value = false;
};
</script>

<template>
  <div @click.stop>
    <div ref="popupRef" class="wrapper">
      <div class="section c-title-bold--m">
        {{ props.word[props.lang] }}
      </div>
      <div class="section" v-if="props.word.description">
        <div class="c-title-bold--m section-title">解説</div>
        <div class="c-text-regular--s">{{ props.word.description }}</div>
      </div>
      <div class="section">
        <div class="c-title-bold--m section-title">週あたりの記事配信数</div>
        <template v-if="isLoading">
          <SkeletonSentence :number-of-lines="5" />
        </template>
        <template v-else>
          <div class="count-wrapper">
            <div class="count">
              <div class="c-title-bold--m doc-type">国内ニュース</div>
              <div class="c-title-bold--m count-per-doc-type">
                {{ articleJaCount }}件
              </div>
            </div>
            <div class="count" v-if="isEnableForeignLangMedia">
              <div class="c-title-bold--m doc-type">海外ニュース</div>
              <div class="c-title-bold--m count-per-doc-type">
                {{ articleEnCount }}件
              </div>
            </div>
            <div class="count" v-if="isTechnicalResearchAvailable">
              <div class="c-title-bold--m doc-type">国内論文</div>
              <div class="c-title-bold--m count-per-doc-type">
                {{ researchPaperJaCount }}件
              </div>
            </div>
            <div
              class="count"
              v-if="isTechnicalResearchAvailable && isEnableForeignLangMedia"
            >
              <div class="c-title-bold--m doc-type">海外論文</div>
              <div class="c-title-bold--m count-per-doc-type">
                {{ researchPaperEnCount }}件
              </div>
            </div>
            <div class="count" v-if="isTechnicalResearchAvailable">
              <div class="c-title-bold--m doc-type">国内特許</div>
              <div class="c-title-bold--m count-per-doc-type">
                {{ patentJaCount }}件
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="section">
        <div class="controls">
          <button
            type="button"
            class="c-btn c-btn--auto c-btn--small"
            :class="
              isDeletedKeywordSelected
                ? 'c-btn--AnewsPrimary'
                : 'delete-keyword-btn'
            "
            @click.stop="
              isDeletedKeywordSelected
                ? restoreKeyword(word.uuid, lang)
                : deleteKeyword(word.uuid, lang)
            "
          >
            {{
              isDeletedKeywordSelected
                ? 'キーワードを元に戻す'
                : 'キーワードを削除'
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  position: fixed;
  z-index: var(--z-tooltip);
  width: 340px;
  background: $color-white;
  border-radius: 4px;
  border: 1px solid $color-gray400;
}
.section {
  padding: 16px 16px;
  border-bottom: 1px solid $color-gray400;
  & > .section-title {
    margin-bottom: 8px;
  }
}
.count-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
  & > .count {
    display: flex;
    justify-content: space-between;
    & > .doc-type {
      color: $color-gray800;
      font-weight: 400;
    }
    & > .count-per-doc-type {
      font-weight: 400;
    }
  }
}
.controls {
  display: flex;
  justify-content: flex-end;
}
.delete-keyword-btn {
  color: $color-white;
  background: $color-orange1000;
  border: 1px solid $color-orange1000;
  &:hover {
    background: $color-orange800;
    border: 1px solid $color-orange800;
  }
}
</style>
